import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Home, Loader2, Calendar, Building2, MapPin,
  StickyNote, Check, Clock, User, AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';

const MobileMyDocketDetail = () => {
  const navigate = useNavigate();
  const { docketId } = useParams();
  const { api } = useMobileAuth();
  const [docket, setDocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef(null);

  const fetchDocket = async () => {
    try {
      const res = await api.get('/api/mobile/my-dockets');
      const found = (res.data || []).find(d => d.id === docketId);
      if (found) {
        setDocket(found);
      } else {
        toast.error('Docket not found');
      }
    } catch (err) {
      console.error('Failed to fetch docket:', err);
      toast.error('Failed to load docket');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (docketId) fetchDocket();
  }, [docketId]);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/api/mobile/dockets/${docketId}/notes`, { text: noteText.trim() });
      setDocket(prev => ({
        ...prev,
        post_submission_notes: [...(prev.post_submission_notes || []), res.data],
      }));
      setNoteText('');
      toast.success('Note added');
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => {
    try { return format(new Date(d + 'T00:00:00'), 'dd MMM yyyy'); }
    catch { return d; }
  };

  const formatDateTime = (d) => {
    try { return format(new Date(d), 'dd MMM yyyy, h:mm a'); }
    catch { return d; }
  };

  const getStatusColor = (s) => {
    if (s === 'approved' || s === 'pending_approval') return 'bg-green-500/20 text-green-400';
    if (s === 'draft') return 'bg-amber-500/20 text-amber-400';
    return 'bg-gray-500/20 text-[var(--m-text-secondary)]';
  };

  if (loading) {
    return (
      <MobileLayout>
        <div className="min-h-screen bg-[var(--m-bg)] flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
        </div>
      </MobileLayout>
    );
  }

  if (!docket) {
    return (
      <MobileLayout>
        <div className="min-h-screen bg-[var(--m-bg)] flex flex-col items-center justify-center">
          <AlertCircle className="h-12 w-12 text-[var(--m-text-muted)] mb-3" />
          <p className="text-[var(--m-text-secondary)]">Docket not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-[#ffd108] text-sm">Go back</button>
        </div>
      </MobileLayout>
    );
  }

  const notes = docket.post_submission_notes || [];

  return (
    <MobileLayout>
      <div className="min-h-screen bg-[var(--m-bg)]" data-testid="my-docket-detail-screen">
        {/* Header */}
        <div className="bg-[var(--m-bg)] border-b border-[var(--m-border)] px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button onClick={() => navigate('/mobile/my-dockets')} className="text-[var(--m-text)]" data-testid="docket-detail-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-[var(--m-text)] font-semibold">Docket #{docket.docket_number || '—'}</h1>
          </div>
          <button onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)]">
            <Home className="h-5 w-5" />
          </button>
        </div>

        {/* Docket Summary */}
        <div className="px-4 pt-4 pb-3 space-y-3">
          <div className="p-3 bg-[var(--m-bg-card)] rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[var(--m-text)] font-medium">#{docket.docket_number || '—'}</span>
              <Badge className={`text-xs ${getStatusColor(docket.status)}`}>
                {(docket.status || 'draft').replace('_', ' ')}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-[var(--m-text-secondary)]">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formatDate(docket.job_date)}</span>
              </div>
              {docket.customer_name && (
                <div className="flex items-center gap-1.5 text-[var(--m-text-secondary)]">
                  <Building2 className="h-3.5 w-3.5" />
                  <span className="truncate">{docket.customer_name}</span>
                </div>
              )}
              {docket.site_name && (
                <div className="flex items-center gap-1.5 text-[var(--m-text-secondary)]">
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="truncate">{docket.site_name}</span>
                </div>
              )}
            </div>
            {docket.notes && (
              <div className="pt-2 border-t border-[var(--m-border)]">
                <p className="text-[11px] text-[var(--m-text-muted)] uppercase tracking-wide mb-1">Original Notes</p>
                <p className="text-sm text-[var(--m-text-body)]">{docket.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Post-Submission Notes */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 mb-3">
            <StickyNote className="h-4 w-4 text-amber-400" />
            <h3 className="text-[var(--m-text)] font-medium text-sm">Post-Submission Notes</h3>
            {notes.length > 0 && (
              <span className="text-xs text-[var(--m-text-muted)]">({notes.length})</span>
            )}
          </div>

          {notes.length === 0 ? (
            <div className="p-4 bg-[var(--m-bg-card)] rounded-lg text-center">
              <p className="text-[var(--m-text-muted)] text-sm">No post-submission notes yet</p>
              <p className="text-gray-600 text-xs mt-1">Add a note below if you need to flag something</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg"
                  data-testid={`post-note-${note.id}`}
                >
                  <p className="text-sm text-[var(--m-text-body)]">{note.text}</p>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--m-text-muted)]">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {note.author_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDateTime(note.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Note Input */}
        <div className="px-4 pb-32">
          <div className="bg-[var(--m-bg-card)] rounded-lg p-3">
            <textarea
              ref={textareaRef}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a post-submission note..."
              rows={3}
              className="w-full bg-transparent text-[var(--m-text)] text-sm placeholder:text-[var(--m-text-muted)] resize-none focus:outline-none"
              data-testid="add-note-textarea"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={handleAddNote}
                disabled={!noteText.trim() || submitting}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#ffd108] text-black text-sm font-medium rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="add-note-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save Note
              </button>
            </div>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default MobileMyDocketDetail;
